import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import EmojiPicker from 'emoji-picker-react';
import DOMPurify from 'dompurify';
import ConfirmModal from '../../components/modals/ConfirmModal';

const BroadcastPage = () => {
  // Get current year dates
  const currentYear = new Date().getFullYear();
  const yearStart = `${currentYear}-01-01`;
  const yearEnd = `${currentYear}-12-31`;
  
  // Main state
  const [broadcasts, setBroadcasts] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter state - arrays for multi-select checkboxes
  const [dateFrom, setDateFrom] = useState(yearStart);
  const [dateTo, setDateTo] = useState(yearEnd);
  const [channelFilters, setChannelFilters] = useState(['email', 'mobile_app', 'sms']);
  const [statusFilters, setStatusFilters] = useState(['scheduled', 'sent', 'draft', 'sending', 'failed', 'partial', 'cancelled']);
  const [typeFilters, setTypeFilters] = useState(['essential', 'marketing', 'platform_updates']);
  const [targetFilters, setTargetFilters] = useState(['admins', 'users']);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [showNewBroadcastModal, setShowNewBroadcastModal] = useState(false);
  const [showSelectTargetsModal, setShowSelectTargetsModal] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [viewingBroadcast, setViewingBroadcast] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [broadcastToDelete, setBroadcastToDelete] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [broadcastToCancel, setBroadcastToCancel] = useState(null);
  const [showRetryConfirm, setShowRetryConfirm] = useState(false);
  const [broadcastToRetry, setBroadcastToRetry] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const fetchBroadcasts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };

      // Only add filters if not all selected
      const allStatuses = ['scheduled', 'sent', 'draft', 'sending', 'failed', 'partial', 'cancelled'];
      const allChannels = ['email', 'mobile_app', 'sms'];
      const allTypes = ['essential', 'marketing', 'platform_updates'];
      const allTargets = ['admins', 'users'];
      
      if (statusFilters.length > 0 && statusFilters.length < allStatuses.length) {
        params.status = statusFilters.join(',');
      }
      if (channelFilters.length > 0 && channelFilters.length < allChannels.length) {
        params.channel = channelFilters.join(',');
      }
      if (typeFilters.length > 0 && typeFilters.length < allTypes.length) {
        params.broadcast_type = typeFilters.join(',');
      }
      if (targetFilters.length > 0 && targetFilters.length < allTargets.length) {
        params.target = targetFilters.join(',');
      }
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (searchQuery) params.search = searchQuery;

      const response = await axios.get(`${API}/super-admin/broadcasts`, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      // Transform backend fields for display
      const transformed = (response.data.broadcasts || []).map(b => ({
        ...b,
        broadcastType: b.broadcast_type,  // Transform snake_case to camelCase for frontend
        date: b.scheduled_date || (b.created_at ? b.created_at.split('T')[0] : ''),
        time: b.scheduled_time || (b.created_at ? new Date(b.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '')
      }));

      setBroadcasts(transformed);
      setTotal(response.data.total || 0);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch broadcasts:', err);
      
      if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
        setTimeout(() => window.location.href = '/super-admin/login', 2000);
      } else if (err.response?.status === 403) {
        setError('Access denied.');
      } else if (err.response?.status === 429) {
        setError('Too many requests. Please wait.');
      } else if (!err.response) {
        setError('Network error. Check connection.');
      } else {
        setError(err.response?.data?.detail || 'Failed to load');
      }
    } finally {
      setLoading(false);
    }
  }, [channelFilters, statusFilters, typeFilters, targetFilters, dateFrom, dateTo, searchQuery, page]);

  useEffect(() => {
    fetchOrganisations();
  }, []);

  useEffect(() => {
    fetchBroadcasts();
  }, [fetchBroadcasts]);


  const fetchOrganisations = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/managed-organisations`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setOrganisations(response.data || []);
    } catch (err) {
      console.error('Failed to fetch organisations:', err);
    } finally {
      setLoading(false);
    }
  };


  const filterBroadcasts = () => {
    return broadcasts.filter(broadcast => {
      // Date range filter
      if (dateFrom && broadcast.date) {
        const broadcastDate = new Date(broadcast.date);
        const fromDate = new Date(dateFrom);
        if (broadcastDate < fromDate) return false;
      }
      if (dateTo && broadcast.date) {
        const broadcastDate = new Date(broadcast.date);
        const toDate = new Date(dateTo);
        if (broadcastDate > toDate) return false;
      }
      
      // Channel filter (multi-select)
      if (channelFilters.length > 0 && !channelFilters.includes(broadcast.channel)) return false;
      
      // Status filter (multi-select)
      if (statusFilters.length > 0 && !statusFilters.includes(broadcast.status)) return false;
      
      // Type filter (multi-select)
      if (typeFilters.length > 0 && !typeFilters.includes(broadcast.broadcastType)) return false;
      
      // Target filter (multi-select)
      if (targetFilters.length > 0 && !targetFilters.includes(broadcast.target)) return false;
      
      // Search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const searchableFields = [
          broadcast.id,
          broadcast.subject,
          broadcast.channel,
          broadcast.status,
          broadcast.broadcastType,
          broadcast.target
        ].join(' ').toLowerCase();
        
        if (!searchableFields.includes(query)) return false;
      }
      
      return true;
    });
  };

  const getChannelBadge = (channel) => {
    const configs = {
      email: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200', icon: '📧', label: 'Email' },
      mobile_app: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200', icon: '📱', label: 'Mobile' },
      sms: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200', icon: '💬', label: 'SMS' }
    };
    const config = configs[channel] || configs.email;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border ${config.bg} ${config.text} ${config.border}`}>
        <span>{config.icon}</span>
        <span>{config.label}</span>
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const configs = {
      scheduled: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200' },
      sent: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
      draft: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200' },
      sending: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
      failed: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' },
      partial: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-300' }
    };
    const config = configs[status] || configs.draft;
    return (
      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold border ${config.bg} ${config.text} ${config.border}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const configs = {
      essential: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' },
      marketing: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
      platform_updates: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200' }
    };
    const config = configs[type] || configs.essential;
    const label = type.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    return (
      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold border ${config.bg} ${config.text} ${config.border}`}>
        {label}
      </span>
    );
  };

  const filteredBroadcasts = filterBroadcasts();

  // Table action handlers
  const sanitizeCSV = (value) => {
    const stringValue = String(value || '');
    if (stringValue.match(/^[=+\-@]/)) {
      return `'${stringValue}`;
    }
    return stringValue;
  };

  const handleViewBroadcast = async (broadcastId) => {
    try {
      const response = await axios.get(`${API}/super-admin/broadcasts/${broadcastId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setViewingBroadcast(response.data);
    } catch (err) {
      console.error('Failed to fetch broadcast:', err);
      alert(err.response?.data?.detail || 'Failed to load broadcast details');
    }
  };

  const handleDuplicateBroadcast = async (broadcastId) => {
    try {
      const response = await axios.post(`${API}/super-admin/broadcasts/${broadcastId}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      alert(`Broadcast duplicated! New ID: ${response.data.new_broadcast_id}`);
      fetchBroadcasts();
    } catch (err) {
      console.error('Failed to duplicate:', err);
      alert(err.response?.data?.detail || 'Failed to duplicate broadcast');
    }
  };

  const handleDeleteBroadcast = (broadcast) => {
    if (broadcast.status !== 'draft' && broadcast.status !== 'failed') {
      alert('Can only delete draft or failed broadcasts');
      return;
    }
    setBroadcastToDelete(broadcast);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/super-admin/broadcasts/${broadcastToDelete.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setShowDeleteConfirm(false);
      setBroadcastToDelete(null);
      alert('Broadcast deleted successfully');
      fetchBroadcasts();
    } catch (err) {
      console.error('Failed to delete:', err);
      alert(err.response?.data?.detail || 'Failed to delete broadcast');
    }
  };

  const handleCancelBroadcast = (broadcast) => {
    if (broadcast.status !== 'scheduled') {
      alert('Can only cancel scheduled broadcasts');
      return;
    }
    setBroadcastToCancel(broadcast);
    setShowCancelConfirm(true);
  };

  const confirmCancel = async () => {
    try {
      await axios.post(`${API}/super-admin/broadcasts/${broadcastToCancel.id}/cancel`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setShowCancelConfirm(false);
      setBroadcastToCancel(null);
      alert('Broadcast cancelled successfully');
      fetchBroadcasts();
    } catch (err) {
      console.error('Failed to cancel:', err);
      alert(err.response?.data?.detail || 'Failed to cancel broadcast');
    }
  };

  const handleRetryBroadcast = (broadcast) => {
    if (broadcast.status !== 'failed' && broadcast.status !== 'partial') {
      alert('Can only retry failed or partial broadcasts');
      return;
    }
    if (broadcast.retry_count >= (broadcast.max_retries || 3)) {
      alert('Maximum retry attempts exceeded');
      return;
    }
    setBroadcastToRetry(broadcast);
    setShowRetryConfirm(true);
  };

  const confirmRetry = async () => {
    try {
      await axios.post(`${API}/super-admin/broadcasts/${broadcastToRetry.id}/retry`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setShowRetryConfirm(false);
      setBroadcastToRetry(null);
      alert('Broadcast retry initiated');
      fetchBroadcasts();
    } catch (err) {
      console.error('Failed to retry:', err);
      alert(err.response?.data?.detail || 'Failed to retry broadcast');
    }
  };

  const handleExportBroadcasts = () => {
    try {
      const dataToExport = filteredBroadcasts.map(b => ({
        'Broadcast ID': sanitizeCSV(b.id),
        'Subject': sanitizeCSV(b.subject),
        'Channel': sanitizeCSV(b.channel),
        'Type': sanitizeCSV(b.broadcast_type),
        'Status': sanitizeCSV(b.status),
        'Target': sanitizeCSV(b.target),
        'Organisations': sanitizeCSV(b.target_organisation_count || 0),
        'Recipients': sanitizeCSV(b.actual_recipient_count || b.scheduled_recipient_count || 0),
        'Opted Out': sanitizeCSV(b.opted_out_count || 0),
        'Date': sanitizeCSV(b.date),
        'Time': sanitizeCSV(b.time),
        'Created': sanitizeCSV(b.created_at)
      }));
      
      const headers = Object.keys(dataToExport[0] || {});
      const csv = [
        headers.join(','),
        ...dataToExport.map(row => 
          headers.map(header => {
            const value = row[header] || '';
            return value.toString().includes(',') 
              ? `"${value.toString().replace(/"/g, '""')}"` 
              : value;
          }).join(',')
        )
      ].join('\n');
      
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `broadcasts_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      alert(`Exported ${dataToExport.length} broadcasts to CSV`);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export data');
    }
  };



  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold">Broadcast Messages</h1>
              <p className="text-purple-200 text-sm">Send announcements via Email, Mobile App, or SMS</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleExportBroadcasts}
              className="px-4 py-2 border border-purple-300 text-white rounded-lg hover:bg-purple-500 font-medium flex items-center gap-2 transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export CSV
            </button>
            <button
              onClick={() => setShowNewBroadcastModal(true)}
              className="px-6 py-2.5 bg-white text-purple-900 rounded-lg font-semibold hover:bg-purple-50 transition-all shadow-lg"
            >
              + New Broadcast
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="grid grid-cols-6 gap-4">
          {/* Date Range */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
            />
          </div>
          
          {/* Channel Filter (Checkboxes) */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Broadcast Channel</label>
            <div className="space-y-1">
              {[
                { value: 'email', label: '📧 Email' },
                { value: 'mobile_app', label: '📱 Mobile' },
                { value: 'sms', label: '💬 SMS' }
              ].map(option => (
                <label key={option.value} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-gray-50 px-2 py-1 rounded">
                  <input
                    type="checkbox"
                    checked={channelFilters.includes(option.value)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setChannelFilters([...channelFilters, option.value]);
                      } else {
                        setChannelFilters(channelFilters.filter(c => c !== option.value));
                      }
                    }}
                    className="w-3 h-3 text-purple-600 rounded"
                  />
                  <span className="text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
          </div>
          
          {/* Status Filter (Checkboxes) */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Status</label>
            <div className="space-y-1">
              {[
                { value: 'scheduled', label: 'Scheduled' },
                { value: 'sent', label: 'Sent' },
                { value: 'draft', label: 'Draft' },
                { value: 'sending', label: 'Sending' },
                { value: 'failed', label: 'Failed' },
                { value: 'partial', label: 'Partial' },
                { value: 'cancelled', label: 'Cancelled' }
              ].map(option => (
                <label key={option.value} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-gray-50 px-2 py-1 rounded">
                  <input
                    type="checkbox"
                    checked={statusFilters.includes(option.value)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setStatusFilters([...statusFilters, option.value]);
                      } else {
                        setStatusFilters(statusFilters.filter(s => s !== option.value));
                      }
                    }}
                    className="w-3 h-3 text-purple-600 rounded"
                  />
                  <span className="text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
          </div>
          
          {/* Type Filter (Checkboxes) */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Broadcast Type</label>
            <div className="space-y-1">
              {[
                { value: 'essential', label: 'Essential' },
                { value: 'marketing', label: 'Marketing' },
                { value: 'platform_updates', label: 'Updates' }
              ].map(option => (
                <label key={option.value} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-gray-50 px-2 py-1 rounded">
                  <input
                    type="checkbox"
                    checked={typeFilters.includes(option.value)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setTypeFilters([...typeFilters, option.value]);
                      } else {
                        setTypeFilters(typeFilters.filter(t => t !== option.value));
                      }
                    }}
                    className="w-3 h-3 text-purple-600 rounded"
                  />
                  <span className="text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
          </div>
          
          {/* Target Filter (Checkboxes) */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Target</label>
            <div className="space-y-1">
              {[
                { value: 'admins', label: 'Admins' },
                { value: 'users', label: 'Users' }
              ].map(option => (
                <label key={option.value} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-gray-50 px-2 py-1 rounded">
                  <input
                    type="checkbox"
                    checked={targetFilters.includes(option.value)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setTargetFilters([...targetFilters, option.value]);
                      } else {
                        setTargetFilters(targetFilters.filter(t => t !== option.value));
                      }
                    }}
                    className="w-3 h-3 text-purple-600 rounded"
                  />
                  <span className="text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        
        {/* Search Row */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <label className="block text-xs font-medium text-gray-700 mb-1">Search</label>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
          />
        </div>
      </div>

      {/* Broadcasts Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Bcast Channel</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Time</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Subject</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Broadcast Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Target</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredBroadcasts.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                      <p>No broadcasts found</p>
                      <p className="text-sm text-gray-400 mt-1">Try adjusting your filters or create a new broadcast</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredBroadcasts.map((broadcast) => (
                  <tr key={broadcast.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">{getChannelBadge(broadcast.channel)}</td>
                    <td className="px-4 py-3">{getStatusBadge(broadcast.status)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{broadcast.date || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{broadcast.time || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {broadcast.subject.length > 50 ? broadcast.subject.substring(0, 50) + '...' : broadcast.subject}
                    </td>
                    <td className="px-4 py-3">{getTypeBadge(broadcast.broadcastType)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 capitalize">{broadcast.target}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {/* View - Always available */}
                        <button
                          onClick={() => handleViewBroadcast(broadcast.id)}
                          className="p-1.5 text-gray-600 hover:text-purple-600 transition-colors"
                          title="View Details"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        
                        {/* Duplicate - Always available */}
                        <button
                          onClick={() => handleDuplicateBroadcast(broadcast.id)}
                          className="p-1.5 text-gray-600 hover:text-blue-600 transition-colors"
                          title="Duplicate as Draft"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                        
                        {/* Cancel - Only for scheduled */}
                        {broadcast.status === 'scheduled' && (
                          <button
                            onClick={() => handleCancelBroadcast(broadcast)}
                            className="p-1.5 text-gray-600 hover:text-orange-600 transition-colors"
                            title="Cancel Scheduled Broadcast"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                        
                        {/* Retry - Only for failed/partial */}
                        {(broadcast.status === 'failed' || broadcast.status === 'partial') && (
                          <button
                            onClick={() => handleRetryBroadcast(broadcast)}
                            className="p-1.5 text-gray-600 hover:text-green-600 transition-colors"
                            title="Retry Broadcast"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </button>
                        )}
                        
                        {/* Delete - Only for draft/failed */}
                        <button
                          onClick={() => handleDeleteBroadcast(broadcast)}
                          disabled={broadcast.status !== 'draft' && broadcast.status !== 'failed'}
                          className={`p-1.5 transition-colors ${
                            broadcast.status === 'draft' || broadcast.status === 'failed'
                              ? 'text-gray-600 hover:text-red-600'
                              : 'text-gray-300 cursor-not-allowed'
                          }`}
                          title={
                            broadcast.status === 'draft' || broadcast.status === 'failed'
                              ? 'Delete Broadcast'
                              : 'Can only delete drafts or failed'
                          }
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {showNewBroadcastModal && (
        <NewBroadcastModal
          organisations={organisations}
          onClose={() => setShowNewBroadcastModal(false)}
          onOpenTargetsModal={() => setShowSelectTargetsModal(true)}
        />
      )}
      
      {/* View Broadcast Details Modal */}
      {viewingBroadcast && (
        <ViewBroadcastDetailsModal
          broadcast={viewingBroadcast}
          onClose={() => setViewingBroadcast(null)}
          getChannelBadge={getChannelBadge}
          getStatusBadge={getStatusBadge}
          getTypeBadge={getTypeBadge}
        />
      )}
      
      {/* Confirmation Modals */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        title="Delete Broadcast"
        message={`Are you sure you want to delete broadcast ${broadcastToDelete?.id}? This cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
      
      <ConfirmModal
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={confirmCancel}
        title="Cancel Broadcast"
        message={`Are you sure you want to cancel broadcast ${broadcastToCancel?.id}? It will not be sent.`}
        confirmText="Cancel Broadcast"
        cancelText="Keep Scheduled"
        variant="warning"
      />
      
      <ConfirmModal
        isOpen={showRetryConfirm}
        onClose={() => setShowRetryConfirm(false)}
        onConfirm={confirmRetry}
        title="Retry Broadcast"
        message={`Retry sending broadcast ${broadcastToRetry?.id}? Attempt ${(broadcastToRetry?.retry_count || 0) + 1} of ${broadcastToRetry?.max_retries || 3}.`}
        confirmText="Retry Now"
        cancelText="Cancel"
        variant="info"
      />
    </div>
  );
};

// New Broadcast Modal Component
const NewBroadcastModal = ({ organisations, onClose, onOpenTargetsModal }) => {
  const [formData, setFormData] = useState({
    channel: 'mobile_app',
    broadcastType: 'essential',
    target: 'users',
    selectedOrganisations: [],
    subject: '',
    message: '',
    sendOption: 'now',
    scheduledDate: null,
    scheduledTime: ''
  });

  const [errors, setErrors] = useState({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showSelectTargetsModal, setShowSelectTargetsModal] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  
  // Custom confirmation modal states
  const [showSaveDraftConfirm, setShowSaveDraftConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const [validationErrorMessage, setValidationErrorMessage] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Broadcast API states
  const [recipientPreview, setRecipientPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);

  // Note: Removed auto-switch restriction - admins can now receive mobile app broadcasts
  // Previously: Auto-switched target to users if channel was mobile_app and target was admins
  // Now: Admins can receive push notifications via mobile app

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (formData.selectedOrganisations.length === 0) newErrors.organisations = 'Please select at least one organisation';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    
    if (formData.sendOption === 'later') {
      if (!formData.scheduledDate) newErrors.scheduledDate = 'Schedule date is required';
      if (!formData.scheduledTime) newErrors.scheduledTime = 'Schedule time is required';
      
      if (formData.scheduledDate && formData.scheduledTime) {
        const scheduledDateTime = new Date(formData.scheduledDate);
        const [hours, minutes] = formData.scheduledTime.split(':');
        scheduledDateTime.setHours(parseInt(hours), parseInt(minutes));
        
        if (scheduledDateTime < new Date()) {
          newErrors.scheduledDateTime = 'Scheduled date/time cannot be in the past';
        }
      }
    }
    
    if (formData.channel === 'sms' && formData.message.length > 160) {
      newErrors.message = 'SMS messages cannot exceed 160 characters';
    }
    
    if (formData.channel === 'mobile_app' && formData.message.length > 500) {
      newErrors.message = 'Mobile notifications cannot exceed 500 characters';
    }
    
    return newErrors;
  };

  const handleConfirm = async () => {
    const validationErrors = validateForm();
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setValidationErrorMessage(Object.values(validationErrors)[0]);
      setShowValidationError(true);
      return;
    }
    
    // Fetch recipient preview
    setLoadingPreview(true);
    try {
      const response = await axios.post(
        `${API}/super-admin/broadcasts/preview-recipients`,
        {
          channel: formData.channel,
          broadcast_type: formData.broadcastType,
          target: formData.target,
          target_organisations: formData.selectedOrganisations.map(org => org.organisation_id)
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      setRecipientPreview(response.data);
      setShowConfirmDialog(true);
    } catch (err) {
      console.error('Failed to preview recipients:', err);
      
      let errorMsg = 'Failed to calculate recipients. Please try again.';
      if (err.response?.status === 401) {
        errorMsg = 'Session expired. Please login again.';
      } else if (err.response?.status === 403) {
        errorMsg = 'Access denied.';
      } else if (err.response?.status === 400) {
        errorMsg = err.response?.data?.detail || 'Invalid request.';
      } else if (!err.response) {
        errorMsg = 'Network error. Check your connection.';
      }
      
      setValidationErrorMessage(errorMsg);
      setShowValidationError(true);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleFinalConfirm = async () => {
    setSending(true);
    setShowConfirmDialog(false);
    
    try {
      // Secure HTML-to-text conversion
      let messagePlain = formData.message;
      if (formData.channel === 'email') {
        const parser = new DOMParser();
        const doc = parser.parseFromString(formData.message, 'text/html');
        messagePlain = doc.body.textContent || doc.body.innerText || formData.message;
      }
      
      const response = await axios.post(
        `${API}/super-admin/broadcasts`,
        {
          channel: formData.channel,
          broadcast_type: formData.broadcastType,
          target: formData.target,
          target_organisations: formData.selectedOrganisations.map(org => org.organisation_id),
          subject: formData.subject,
          message: formData.message,
          message_plain: messagePlain,
          send_option: formData.sendOption,
          scheduled_date: formData.scheduledDate 
            ? (typeof formData.scheduledDate === 'string' 
                ? formData.scheduledDate 
                : new Date(formData.scheduledDate).toISOString().split('T')[0])
            : null,
          scheduled_time: formData.scheduledTime || null
        },
        { 
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          timeout: 30000
        }
      );
      
      // Success message
      let successMsg = '';
      if (response.data.status === 'sending') {
        successMsg = `Broadcast is being sent to ${response.data.eligible_recipients} users now!`;
      } else if (response.data.status === 'scheduled') {
        successMsg = `Broadcast scheduled! Will send to ${response.data.eligible_recipients} users.`;
      } else {
        successMsg = 'Draft saved successfully!';
      }
      
      if (response.data.opted_out_count > 0) {
        successMsg += `\n\n${response.data.opted_out_count} users opted out.`;
      }
      
      setSuccessMessage(successMsg);
      setShowSuccessMessage(true);
      
      onClose();
      setTimeout(() => window.location.reload(), 1500);
      
    } catch (err) {
      console.error('Failed to create broadcast:', err);
      
      let errorMsg = 'Failed to create broadcast';
      if (err.response?.status === 401) {
        errorMsg = 'Session expired. Please login again.';
        setTimeout(() => window.location.href = '/super-admin/login', 2000);
      } else if (err.response?.status === 403) {
        errorMsg = 'Access denied.';
      } else if (err.response?.status === 400) {
        errorMsg = err.response?.data?.detail || 'Invalid data.';
      } else if (err.response?.status === 500) {
        errorMsg = 'Server error. Try again or contact support.';
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMsg = 'Request timeout. Broadcast may still be processing.';
      } else if (!err.response) {
        errorMsg = 'Network error. Check connection.';
      } else {
        errorMsg = err.response?.data?.detail || 'Unexpected error';
      }
      
      setValidationErrorMessage(errorMsg);
      setShowValidationError(true);
      setShowConfirmDialog(false);
    } finally {
      setSending(false);
    }
  };

  const handleSaveDraft = () => {
    setShowSaveDraftConfirm(true);
  };
  
  const handleConfirmSaveDraft = () => {
    console.log('Save draft:', formData);
    setShowSaveDraftConfirm(false);
    setSuccessMessage('Draft saved successfully! (Backend Phase 2)');
    setShowSuccessMessage(true);
  };

  const handleCancel = () => {
    const hasData = formData.subject || formData.message || formData.selectedOrganisations.length > 0;
    if (hasData) {
      setShowCancelConfirm(true);
    } else {
      onClose();
    }
  };
  
  const handleConfirmCancel = () => {
    setShowCancelConfirm(false);
    onClose();
  };

  const handleOverlayClick = (e) => {
    // Backdrop click disabled - modal must be closed via buttons
  };

  const handleLoadSelectedOrgs = (selectedOrgs) => {
    setFormData(prev => ({ ...prev, selectedOrganisations: selectedOrgs }));
    setShowSelectTargetsModal(false);
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ]
  };

  return (
    <>
      <div onClick={handleOverlayClick} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-purple-500 text-white p-6 rounded-t-xl z-10">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">Create New Broadcast</h2>
              <button onClick={handleCancel} className="text-white hover:text-gray-200 transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Form */}
          <div className="p-6 space-y-6">
            {/* 1. Broadcast Channel */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Broadcast Channel *</label>
              <div className="flex gap-4">
                {['email', 'mobile_app', 'sms'].map((channel) => (
                  <label
                    key={channel}
                    className={`flex-1 flex items-center justify-center gap-3 px-4 py-4 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.channel === channel
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-300 hover:border-purple-300 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="channel"
                      value={channel}
                      checked={formData.channel === channel}
                      onChange={(e) => setFormData({ ...formData, channel: e.target.value })}
                      className="w-4 h-4 text-purple-600"
                    />
                    <div className="text-center">
                      <div className="text-2xl mb-1">
                        {channel === 'email' && '📧'}
                        {channel === 'mobile_app' && '📱'}
                        {channel === 'sms' && '💬'}
                      </div>
                      <div className="text-sm font-medium text-gray-900">
                        {channel === 'email' && 'Email'}
                        {channel === 'mobile_app' && 'Mobile App'}
                        {channel === 'sms' && 'SMS'}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* 2. Broadcast Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Broadcast Type *</label>
              <div className="flex gap-4">
                {[
                  { value: 'essential', label: 'Essential', color: 'red', icon: '🔴' },
                  { value: 'marketing', label: 'Marketing', color: 'blue', icon: '🔵' },
                  { value: 'platform_updates', label: 'Platform Updates', color: 'purple', icon: '🟣' }
                ].map((type) => (
                  <label
                    key={type.value}
                    className={`flex-1 flex items-center gap-3 px-4 py-3 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.broadcastType === type.value
                        ? `border-${type.color}-500 bg-${type.color}-50`
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <input
                      type="radio"
                      name="broadcastType"
                      value={type.value}
                      checked={formData.broadcastType === type.value}
                      onChange={(e) => setFormData({ ...formData, broadcastType: e.target.value })}
                      className="w-4 h-4"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{type.icon} {type.label}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* 3. Target Audience */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                Target Audience *
                <div className="group relative">
                  <svg className="w-4 h-4 text-gray-400 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="absolute left-0 top-6 hidden group-hover:block w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-10">
                    Select who should receive this broadcast. Both Organisation Admins and Organisation Users can receive notifications via all channels (Email, SMS, and Mobile App).
                  </div>
                </div>
              </label>
              <div className="flex gap-4">
                <label
                  className={`flex-1 flex items-center gap-3 px-4 py-3 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.target === 'admins'
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <input
                    type="radio"
                    name="target"
                    value="admins"
                    checked={formData.target === 'admins'}
                    onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                    className="w-4 h-4"
                  />
                  <div className="text-sm font-medium text-gray-900">Organisation Admins</div>
                </label>
                <label
                  className={`flex-1 flex items-center gap-3 px-4 py-3 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.target === 'users'
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <input
                    type="radio"
                    name="target"
                    value="users"
                    checked={formData.target === 'users'}
                    onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                    className="w-4 h-4"
                  />
                  <div className="text-sm font-medium text-gray-900">Organisation Users</div>
                </label>
              </div>
            </div>

            {/* 4. Select Targets */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Organisations *</label>
              <button
                type="button"
                onClick={() => setShowSelectTargetsModal(true)}
                className="w-full px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all text-left"
              >
                {formData.selectedOrganisations.length === 0 ? (
                  <span className="text-gray-500">Click to select organisations</span>
                ) : (
                  <div>
                    <div className="font-medium text-gray-900 mb-2">
                      {formData.selectedOrganisations.length} organisation(s) selected
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.selectedOrganisations.slice(0, 20).map(org => (
                        <span key={org.id || org.organisation_id} className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                          {org.name}
                        </span>
                      ))}
                      {formData.selectedOrganisations.length > 20 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowSelectTargetsModal(true);
                          }}
                          className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-full hover:bg-gray-300"
                        >
                          +{formData.selectedOrganisations.length - 20} more
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </button>
              {errors.organisations && <p className="text-xs text-red-600 mt-1">{errors.organisations}</p>}
            </div>

            {/* 5. Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Broadcast Subject *</label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                maxLength={200}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="Enter broadcast subject"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Used in: Email subject line, Mobile notification title, SMS preview</span>
                <span>{formData.subject.length}/200</span>
              </div>
              {errors.subject && <p className="text-xs text-red-600 mt-1">{errors.subject}</p>}
            </div>

            {/* 6. Message (Conditional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Broadcast Message *</label>
              
              {/* Email: Rich Text Editor */}
              {formData.channel === 'email' && (
                <div>
                  <ReactQuill
                    theme="snow"
                    value={formData.message}
                    onChange={(content) => setFormData({ ...formData, message: content })}
                    modules={quillModules}
                    className="bg-white rounded-lg"
                    style={{ height: '300px', marginBottom: '50px' }}
                  />
                  <p className="text-xs text-gray-500 mt-2">Supports formatting, images, and links</p>
                </div>
              )}
              
              {/* SMS or Mobile App: Plain Text with Emoji */}
              {(formData.channel === 'sms' || formData.channel === 'mobile_app') && (
                <div>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={6}
                    maxLength={formData.channel === 'sms' ? 160 : 500}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="Enter your message..."
                  />
                  <div className="flex justify-between items-center mt-2">
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm flex items-center gap-2"
                      >
                        😊 Add Emoji
                      </button>
                      {showEmojiPicker && (
                        <div className="absolute bottom-12 left-0 z-50">
                          <EmojiPicker
                            onEmojiClick={(emojiObject) => {
                              setFormData({ ...formData, message: formData.message + emojiObject.emoji });
                              setShowEmojiPicker(false);
                            }}
                            width={350}
                            height={400}
                          />
                        </div>
                      )}
                    </div>
                    <span className="text-xs text-gray-500">
                      {formData.message.length}/{formData.channel === 'sms' ? 160 : 500}
                      {formData.channel === 'sms' && ' (SMS limit)'}
                    </span>
                  </div>
                </div>
              )}
              {errors.message && <p className="text-xs text-red-600 mt-1">{errors.message}</p>}
            </div>

            {/* 7. Send/Schedule */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Delivery *</label>
              <div className="flex gap-4 mb-4">
                <label
                  className={`flex-1 flex items-center gap-3 px-4 py-3 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.sendOption === 'now'
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <input
                    type="radio"
                    name="sendOption"
                    value="now"
                    checked={formData.sendOption === 'now'}
                    onChange={(e) => setFormData({ ...formData, sendOption: e.target.value })}
                    className="w-4 h-4"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Send Now</div>
                    <div className="text-xs text-gray-600">Broadcast will be sent immediately</div>
                  </div>
                </label>
                <label
                  className={`flex-1 flex items-center gap-3 px-4 py-3 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.sendOption === 'later'
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <input
                    type="radio"
                    name="sendOption"
                    value="later"
                    checked={formData.sendOption === 'later'}
                    onChange={(e) => setFormData({ ...formData, sendOption: e.target.value })}
                    className="w-4 h-4"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Schedule for Later</div>
                    <div className="text-xs text-gray-600">Choose date and time</div>
                  </div>
                </label>
              </div>
              
              {formData.sendOption === 'later' && (
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Schedule Date *</label>
                      <input
                        type="date"
                        min={new Date().toISOString().split('T')[0]}
                        value={formData.scheduledDate ? new Date(formData.scheduledDate).toISOString().split('T')[0] : ''}
                        onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value ? new Date(e.target.value) : null })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                      {errors.scheduledDate && <p className="text-xs text-red-600 mt-1">{errors.scheduledDate}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Schedule Time *</label>
                      <input
                        type="time"
                        value={formData.scheduledTime}
                        onChange={(e) => setFormData({ ...formData, scheduledTime: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                      {errors.scheduledTime && <p className="text-xs text-red-600 mt-1">{errors.scheduledTime}</p>}
                    </div>
                  </div>
                  {formData.scheduledDate && formData.scheduledTime && (
                    <div className="mt-3 text-sm text-purple-700 bg-purple-100 p-2 rounded">
                      📅 Scheduled for: {new Date(formData.scheduledDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} at {formData.scheduledTime}
                    </div>
                  )}
                  {errors.scheduledDateTime && <p className="text-xs text-red-600 mt-2">{errors.scheduledDateTime}</p>}
                </div>
              )}
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200 rounded-b-xl flex justify-end gap-3">
            <button
              type="button"
              onClick={handleSaveDraft}
              className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-white font-medium transition-all"
            >
              💾 Save Draft
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-white font-medium transition-all"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={loadingPreview || sending}
              className="px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingPreview ? 'Calculating recipients...' : sending ? 'Sending...' : `✓ Confirm & ${formData.sendOption === 'now' ? 'Send' : 'Schedule'}`}
            </button>
          </div>
        </div>
      </div>

      {/* Select Targets Modal */}
      {showSelectTargetsModal && (
        <SelectTargetsModal
          organisations={organisations}
          currentSelected={formData.selectedOrganisations}
          onClose={() => setShowSelectTargetsModal(false)}
          onLoad={handleLoadSelectedOrgs}
        />
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <ConfirmationDialog
          formData={formData}
          recipientPreview={recipientPreview}
          onCancel={() => setShowConfirmDialog(false)}
          onConfirm={handleFinalConfirm}
        />
      )}
      
      {/* Custom Confirmation Modals */}
      <ConfirmModal
        isOpen={showSaveDraftConfirm}
        onClose={() => setShowSaveDraftConfirm(false)}
        onConfirm={handleConfirmSaveDraft}
        title="Save as Draft"
        message="Save this broadcast as a draft? You can complete and send it later."
        confirmText="Save Draft"
        cancelText="Cancel"
        variant="info"
      />
      
      <ConfirmModal
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={handleConfirmCancel}
        title="Discard Changes"
        message="You have unsaved changes. Are you sure you want to cancel?"
        confirmText="Yes, Discard"
        cancelText="No, Keep Editing"
        variant="warning"
      />
      
      <ConfirmModal
        isOpen={showValidationError}
        onClose={() => setShowValidationError(false)}
        onConfirm={() => setShowValidationError(false)}
        title="Validation Error"
        message={validationErrorMessage}
        confirmText="OK"
        cancelText=""
        variant="danger"
      />
      
      <ConfirmModal
        isOpen={showSuccessMessage}
        onClose={() => {
          setShowSuccessMessage(false);
          onClose();
        }}
        onConfirm={() => {
          setShowSuccessMessage(false);
          onClose();
        }}
        title="Success"
        message={successMessage}
        confirmText="OK"
        cancelText=""
        variant="success"
      />
    </>
  );
};

// Select Targets Modal Component
const SelectTargetsModal = ({ organisations, currentSelected, onClose, onLoad }) => {
  const [tempSelected, setTempSelected] = useState([]);
  const [tierFilter, setTierFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    // Pre-select current selections
    const currentIds = currentSelected.map(org => org.organisation_id);
    setTempSelected(currentIds);
  }, [currentSelected]);

  const filterOrganisations = () => {
    return organisations.filter(org => {
      // Only active organisations
      if (org.status !== 'Active') return false;
      
      // Tier filter
      if (tierFilter !== 'all' && org.subscription_tier !== tierFilter) return false;
      
      // Location filter
      if (locationFilter !== 'all' && org.city !== locationFilter) return false;
      
      // Search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const searchable = [
          org.name,
          org.organisation_id,
          org.admin_email,
          org.city
        ].join(' ').toLowerCase();
        if (!searchable.includes(query)) return false;
      }
      
      return true;
    });
  };

  const filteredOrgs = filterOrganisations();

  // Get unique cities and tiers
  const uniqueCities = [...new Set(organisations.map(o => o.city).filter(Boolean))];
  const uniqueTiers = [...new Set(organisations.map(o => o.subscription_tier).filter(Boolean))];

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setTempSelected(filteredOrgs.map(org => org.organisation_id));
    } else {
      setTempSelected([]);
    }
  };

  const handleOrgToggle = (orgId, checked) => {
    if (checked) {
      setTempSelected([...tempSelected, orgId]);
    } else {
      setTempSelected(tempSelected.filter(id => id !== orgId));
      setSelectAll(false);
    }
  };

  const handleLoad = () => {
    const selectedOrgs = organisations.filter(org => 
      tempSelected.includes(org.organisation_id)
    );
    onLoad(selectedOrgs);
  };

  const handleOverlayClick = (e) => {
    // Backdrop click disabled - modal must be closed via buttons
  };

  return (
    <div onClick={handleOverlayClick} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-500 text-white p-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-2xl font-bold">Select Target Organisations</h2>
            <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-purple-200 text-sm">{tempSelected.length} organisation(s) selected</p>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Subscription Tier</label>
              <select
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="all">All Tiers</option>
                {uniqueTiers.map(tier => (
                  <option key={tier} value={tier}>{tier}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
              <select
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="all">All Locations</option>
                {uniqueCities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search organisations..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          </div>
        </div>

        {/* Select All */}
        <div className="p-4 border-b border-gray-200 bg-white">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={selectAll}
              onChange={(e) => handleSelectAll(e.target.checked)}
              className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
            />
            <span className="text-sm font-medium text-gray-900">
              Select All ({filteredOrgs.length} organisations)
            </span>
          </label>
        </div>

        {/* Organisation List */}
        <div className="flex-1 overflow-y-auto p-4">
          {filteredOrgs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>No organisations found</p>
              <p className="text-sm text-gray-400 mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredOrgs.map((org) => {
                const orgId = org.organisation_id;
                const isChecked = tempSelected.includes(orgId);
                
                return (
                  <div
                    key={orgId}
                    className={`border rounded-lg p-4 transition-all cursor-pointer ${
                      isChecked
                        ? 'bg-purple-50 border-purple-300'
                        : 'border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                    }`}
                    onClick={() => handleOrgToggle(orgId, !isChecked)}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleOrgToggle(orgId, e.target.checked);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1 w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-semibold text-gray-900">{org.name}</h4>
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
                            {org.subscription_tier || 'Free'}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">📧 {org.admin_email || 'No email'}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          📍 {org.city || 'No location'}
                          {' • '}
                          🆔 {org.organisation_id}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {tempSelected.length} of {organisations.length} organisations selected
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-white font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleLoad}
              className="px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium shadow-lg"
            >
              Load Selected ({tempSelected.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Confirmation Dialog Component
const ConfirmationDialog = ({ formData, recipientPreview, onCancel, onConfirm }) => {
  const handleOverlayClick = (e) => {
    // Backdrop click disabled - modal must be closed via buttons
  };

  const getChannelName = (channel) => {
    return { email: 'Email', mobile_app: 'Mobile App Notification', sms: 'SMS' }[channel] || channel;
  };

  const getBroadcastTypeName = (type) => {
    return type.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const getScheduleText = () => {
    if (formData.sendOption === 'now') {
      return 'Send Immediately';
    }
    if (formData.scheduledDate) {
      const dateObj = typeof formData.scheduledDate === 'string' ? new Date(formData.scheduledDate) : formData.scheduledDate;
      return `${dateObj.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} at ${formData.scheduledTime}`;
    }
    return 'Not scheduled';
  };

  return (
    <div onClick={handleOverlayClick} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Confirm Broadcast</h3>
              <p className="text-sm text-gray-600">Review before sending</p>
            </div>
          </div>
        </div>
        
        <div className="p-6 space-y-3">
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">📡 Channel</span>
              <span className="text-sm font-semibold text-gray-900">{getChannelName(formData.channel)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">📋 Type</span>
              <span className="text-sm font-semibold text-gray-900">{getBroadcastTypeName(formData.broadcastType)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">🎯 Target</span>
              <span className="text-sm font-semibold text-gray-900 capitalize">{formData.target}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">🏢 Organisations</span>
              <span className="text-sm font-semibold text-gray-900">{formData.selectedOrganisations.length}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">✉️ Subject</span>
              <span className="text-sm font-semibold text-gray-900">{formData.subject}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">⏰ Schedule</span>
              <span className="text-sm font-semibold text-gray-900">{getScheduleText()}</span>
            </div>
            
            {/* Recipient Information */}
            {recipientPreview && (
              <>
                <div className="mt-3 pt-3 border-t border-gray-300"></div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">👥 Total Users</span>
                  <span className="text-sm font-semibold text-gray-900">{recipientPreview.total_users}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">✅ Will Receive</span>
                  <span className="text-sm font-semibold text-green-600">{recipientPreview.eligible_recipients}</span>
                </div>
                {recipientPreview.opted_out_count > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">⛔ Opted Out</span>
                    <span className="text-sm font-semibold text-yellow-600">
                      {recipientPreview.opted_out_count} ({recipientPreview.opt_out_percentage}%)
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
          
          {/* Warning for high opt-out rate */}
          {recipientPreview && recipientPreview.opt_out_percentage > 20 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex gap-2">
                <svg className="w-5 h-5 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-yellow-900">High Opt-Out Rate</p>
                  <p className="text-xs text-yellow-800 mt-1">
                    {recipientPreview.opt_out_percentage}% of users have opted out. Consider reviewing broadcast content.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {formData.sendOption === 'now' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex gap-2">
                <svg className="w-5 h-5 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-yellow-900">Send Immediately</p>
                  <p className="text-xs text-yellow-800 mt-1">
                    This broadcast will be sent to all selected recipients right away. This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
          >
            Go Back
          </button>
          <button
            onClick={onConfirm}
            className="px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold shadow-lg"
          >
            ✓ Confirm & {formData.sendOption === 'now' ? 'Send Now' : 'Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
};


// View Broadcast Details Modal Component
const ViewBroadcastDetailsModal = ({ broadcast, onClose, getChannelBadge, getStatusBadge, getTypeBadge }) => {
  if (!broadcast) return null;
  
  const handleOverlayClick = (e) => {
    // Backdrop click disabled - modal must be closed via buttons
  };
  
  return (
    <div onClick={handleOverlayClick} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-purple-500 text-white p-6 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold font-mono">{broadcast.id}</h2>
              <p className="text-purple-100 text-sm mt-1">{broadcast.subject}</p>
            </div>
            <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4 pb-4 border-b border-gray-200">
            <div>
              <p className="text-xs text-gray-600 mb-1">Channel</p>
              <div>{getChannelBadge(broadcast.channel)}</div>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Status</p>
              <div>{getStatusBadge(broadcast.status)}</div>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Broadcast Type</p>
              <div>{getTypeBadge(broadcast.broadcast_type)}</div>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Target Audience</p>
              <p className="font-medium capitalize">{broadcast.target}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Created</p>
              <p className="font-medium">{broadcast.created_at ? new Date(broadcast.created_at).toLocaleString() : '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Sent</p>
              <p className="font-medium">{broadcast.sent_at ? new Date(broadcast.sent_at).toLocaleString() : '-'}</p>
            </div>
          </div>

          {/* Recipient Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Recipient Summary</h3>
            <div className="grid grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-gray-900">{broadcast.scheduled_recipient_count || 0}</p>
                <p className="text-xs text-gray-600">Scheduled</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{broadcast.actual_recipient_count || 0}</p>
                <p className="text-xs text-gray-600">Delivered To</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-600">{broadcast.opted_out_count || 0}</p>
                <p className="text-xs text-gray-600">Opted Out</p>
              </div>
              <div>
                <p className={`text-2xl font-bold ${broadcast.recipient_delta > 0 ? 'text-green-600' : broadcast.recipient_delta < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                  {broadcast.recipient_delta > 0 ? '+' : ''}{broadcast.recipient_delta || 0}
                </p>
                <p className="text-xs text-gray-600">Delta</p>
              </div>
            </div>
          </div>

          {/* Delivery Stats */}
          {broadcast.delivery_stats && broadcast.status !== 'draft' && broadcast.status !== 'scheduled' && (
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Delivery Statistics</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-lg font-bold text-blue-600">
                    {broadcast.delivery_stats[broadcast.channel]?.attempted || 0}
                  </p>
                  <p className="text-xs text-gray-600">Attempted</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-green-600">
                    {broadcast.delivery_stats[broadcast.channel]?.delivered || 0}
                  </p>
                  <p className="text-xs text-gray-600">Delivered</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-red-600">
                    {broadcast.delivery_stats[broadcast.channel]?.failed || 0}
                  </p>
                  <p className="text-xs text-gray-600">Failed</p>
                </div>
              </div>
              {broadcast.execution_duration_seconds && (
                <p className="text-xs text-gray-600 mt-3 text-center">
                  Execution time: {broadcast.execution_duration_seconds}s
                </p>
              )}
            </div>
          )}

          {/* Message Content */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Message Content</h3>
            {broadcast.channel === 'email' ? (
              <div 
                className="border border-gray-200 rounded-lg p-4 max-h-64 overflow-y-auto prose max-w-none"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(broadcast.message) }}
              />
            ) : (
              <div className="border border-gray-200 rounded-lg p-4 whitespace-pre-wrap max-h-64 overflow-y-auto">
                {broadcast.message_plain}
              </div>
            )}
          </div>

          {/* Errors */}
          {broadcast.errors && broadcast.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-semibold text-red-900 mb-2">Errors ({broadcast.errors.length})</h3>
              <ul className="text-sm text-red-800 space-y-1 max-h-32 overflow-y-auto">
                {broadcast.errors.map((error, idx) => (
                  <li key={idx}>• {error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


export default BroadcastPage;
